'use client';

import { useTranslations } from 'next-intl';
import { useRouter } from '@/i18n/routing';
import { toast } from 'sonner';
import { QrCode } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { useHardwareScanner } from '@/hooks/use-hardware-scanner';
import { useHashDialog } from '@/hooks/use-hash-dialog';
import { useRouteAccess } from '@/hooks/use-route-access';
import { IS_QR_SCANNER_ENABLED } from '@/lib/feature-flags';
import { GLOBAL_SCANNER_HASH, SCANNER_ENTITY_ROUTES } from '../constants';
import { useQrResolveMutation } from '../services/global-scanner.mutations';
import { GlobalScannerModal } from './global-scanner-modal';

export function GlobalScannerButton() {
  const t = useTranslations('globalScanner');
  const tg = useTranslations('auth.guard');
  const router = useRouter();
  const canAccess = useRouteAccess();
  // #scanner — app-shell overlay open-state in the URL hash.
  const { open: scannerOpen, onOpenChange: toggleScanner } = useHashDialog(
    GLOBAL_SCANNER_HASH.scan
  );

  const { mutate: resolveQr } = useQrResolveMutation();

  // Hardware scanner fires globally when no input is focused and modal is closed.
  // Stays active even when the camera UI is hidden — hardware scanners don't need it.
  useHardwareScanner({
    enabled: !scannerOpen,
    onScan: (value) => {
      resolveQr(value, {
        onSuccess: (result) => {
          // Same rule as the modal: a hardware scan can resolve to a module
          // this account cannot open, and pushing there lands on Access Denied.
          const target = SCANNER_ENTITY_ROUTES[result.entity_type](
            result.entity_id,
            result.label
          );
          if (!canAccess(target)) {
            toast.error(tg('noPermission'));
            return;
          }
          router.push(target);
        },
        onError: () => {
          toast.error(t('notFound'));
        }
      });
    }
  });

  if (!IS_QR_SCANNER_ENABLED) return null;

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant='ghost'
            size='icon'
            onClick={() => toggleScanner(true)}
            aria-label={t('title')}
          >
            <QrCode className='h-5 w-5' />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('title')}</TooltipContent>
      </Tooltip>

      <GlobalScannerModal open={scannerOpen} onOpenChange={toggleScanner} />
    </>
  );
}
